import React from 'react';
import styled from 'styled-components';

import { seriesRamp, seriesRampWithOther, seriesStop, tokens } from 'containers/Intelligence/theme';

import { hiddenRowCount, IListViewMore, inlineRowsFor, ListViewMoreFooter } from '../ListViewMore';
import { formatSharePercent, sharePercent } from '../formatMetric';

export type RankedBarListDensity = 'compact' | 'breakdown';

export interface IRankedBarListItem {
  label: string;
  subLabel?: string;
  /**
   * Pre-formatted by the caller — a ranked row's value is a count, a sum or a currency amount.
   * Optional because a distribution the design states only as percentages has no count to print, and
   * deriving one from a total the design never shows would be inventing data.
   */
  value?: string;
  /**
   * Share of the whole as a 0–1 fraction, the same scale as `LegendTable.pct`; absent means the
   * share was never measured, and the row then prints its value without one.
   */
  pct?: number;
  /** A `tokens.color` value; without it every bar is painted in the family's ranked-bar fill. */
  color?: string;
}

export interface IRankedBarListProps {
  items: IRankedBarListItem[];
  density: RankedBarListDensity;
  /**
   * Prints the share beside the value. Off for a ranking whose screen draws the bar alone —
   * Expectation Gap Drivers prints `+11` and no percentage — where the share still sets the bar's
   * width. Defaults on, which is every other ranking on the page.
   */
  printShare?: boolean;
  /**
   * Width of the compact density's fixed label column. Defaults to the 82px Customer Base and
   * Experience & Value were measured at; `2533:1670` draws its evidence rows at 112, and a label
   * that does not fit its column wraps and doubles the row's height.
   */
  labelColumnWidth?: string;
  /**
   * Fill hues, keyed to row position and cycled. Defaults to the compact density's own palette; the
   * industry board draws two of its own, exported as `BOARD_RISK_PALETTE` and `BOARD_RAMP_PALETTE`.
   * Ignored by the breakdown density, which the screens paint in one flat fill.
   */
  palette?: string[];
  /**
   * Track height and corner radius. Defaulted per density to what Customer Base and Experience &
   * Value were measured at, because those two bands share this component and were read off their own
   * frames — `2533:1670` draws 7px at radius 8 and must not move them.
   */
  trackHeight?: string;
  trackRadius?: string;
  /**
   * Label/value type overrides. The two densities' own sizes are read off Customer Base / Experience
   * & Value (compact) and the modal breakdowns (breakdown) — a caller whose own frame measures
   * something else (Suppression Reasons, `589:29772`, is 9px/lh 1 against compact's 11px/17px) passes
   * it here rather than nudging either density's shared default under every other consumer.
   */
  labelFontSize?: string;
  labelLineHeight?: string;
  valueFontSize?: string;
  valueLineHeight?: string;
  /** Defaults to `text-primary`; Suppression Reasons reads its count in the muted ink instead. */
  valueColor?: string;
  /**
   * Caps the ranking at six rows and moves the rest behind a `View more` overlay. Omitted, the list
   * draws every row it is handed, exactly as before: the overlay is titled by the block's own
   * heading, which a row array does not carry. PLA-8844.
   */
  viewMore?: IListViewMore;
  className?: string;
}

const TRACK_HEIGHTS: Record<RankedBarListDensity, string> = {
  compact: '8px',
  breakdown: '10px'
};

const TRACK_RADII: Record<RankedBarListDensity, string> = {
  compact: tokens.radius.xs,
  breakdown: tokens.radius.pill
};

const LABEL_COLUMN_WIDTH = '82px';

// The mockup draws the breakdown bar in #4F46E5 on a #1A2440 track, neither of which is a token and
// neither of which may become one (PLA-8815 plan.md AF-3): both map to their nearest named token.
// Round 5: the single breakdown fill is the ramp's strongest stop; the fallback is the hue above.
const BAR_FILL = seriesStop(1, tokens.color['accent-purple']);

/**
 * A compact ranking is drawn one hue per rank rather than in a single fill, so the row a reader is
 * on stays identifiable once the bars shorten down the list. The breakdown density keeps `BAR_FILL`:
 * its rows are read one at a time and the mockup paints them all alike.
 */
/* The two palettes `2533:1670` keys to ROW POSITION, read node by node off all seven of its ranked
   blocks. Both are position-keyed, not value-keyed: the same percentage takes a different hue
   depending on where it sits in the list.

   Six of the seven hues are existing tokens exactly. The seventh, `#8B96AB`, is not a token and does
   not become one — `accent-unknown` `#8994AA` is its nearest named token, which is the convention
   this component already records for `#4F46E5` / `#1A2440` (PLA-8815 plan.md AF-3). Cited rather
   than judged: the rule decides it, not the 2-per-channel distance.

   RISK is used where the rows carry risk semantics — Telco SERVICE-RISK EVIDENCE and TOP CHURN
   DRIVERS, Insurance TOP RENEWAL-RISK DRIVERS. RAMP is used by the distribution, adoption and gap
   blocks, truncated to the row count; its six stops are read off WALLET FEATURE ADOPTION RATE
   (`2570:4972`..`5007`), the only block with six rows.

   One outlier, deliberately NOT smoothed: Telco UPGRADE-READINESS EVIDENCE takes RAMP stops 1-3 and
   then `#EE6475` at stop 4, where RAMP would give `#1EB980`. Its fourth row is `Risk guardrails`, so
   it reads as intentional. Raised as a designer question rather than assumed either way. */
const BOARD_RISK_PALETTE = seriesRampWithOther([
  tokens.color.negative,
  tokens.color.warning,
  tokens.color['accent-purple'],
  tokens.color['accent-unknown']
]);

const BOARD_RAMP_PALETTE = seriesRampWithOther([
  tokens.color['accent-purple'],
  tokens.color['accent-blue'],
  tokens.color['accent-cyan'],
  tokens.color.positive,
  tokens.color.warning,
  tokens.color['accent-unknown']
]);

const COMPACT_BAR_PALETTE = seriesRamp([
  tokens.color['accent-neutral'],
  tokens.color['accent-blue'],
  tokens.color['accent-purple'],
  tokens.color['accent-cyan'],
  tokens.color.positive
]);

const List = styled.ul<{ $density: RankedBarListDensity }>`
  margin: 0;
  padding: 0;
  list-style: none;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${({ $density }) =>
      $density === 'breakdown' ? tokens.space['16'] : tokens.space['4']};
  }
`;

const Row = styled.li<{ $density: RankedBarListDensity }>`
  display: flex;
  align-items: ${({ $density }) => ($density === 'breakdown' ? 'flex-start' : 'center')};
  flex-wrap: ${({ $density }) => ($density === 'breakdown' ? 'wrap' : 'nowrap')};
`;

const RowText = styled.div<{ $density: RankedBarListDensity; $labelColumn: string }>`
  min-width: 0;
  ${({ $density, $labelColumn }) =>
    $density === 'breakdown' ? 'flex: 1 1 auto;' : `flex: 0 0 ${$labelColumn};`}
`;

const RowLabel = styled.span<{
  $density: RankedBarListDensity;
  $fontSize?: string;
  $lineHeight?: string;
}>`
  display: block;
  color: ${tokens.color['text-primary']};
  font-size: ${({ $density, $fontSize }) =>
    $fontSize ??
    ($density === 'breakdown' ? tokens.type['card-title-size'] : tokens.type['label-size'])};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${({ $density, $lineHeight }) =>
    $lineHeight ??
    ($density === 'breakdown' ? tokens.type['card-title-line'] : tokens.type['label-line'])};
`;

const RowSubLabel = styled.span`
  display: block;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['label-line']};
`;

/* Both densities keep one DOM order (text, track, value); `order` moves the breakdown track onto
   its own full-width line beneath the two, which is where the modal variant draws it. */
const Track = styled.div<{
  $density: RankedBarListDensity;
  $height?: string;
  $radius?: string;
}>`
  position: relative;
  overflow: hidden;
  height: ${({ $density, $height }) => $height ?? TRACK_HEIGHTS[$density]};
  border-radius: ${({ $density, $radius }) => $radius ?? TRACK_RADII[$density]};
  background: ${tokens.color['track-bg']};

  ${({ $density }) =>
    $density === 'breakdown'
      ? `
    order: 2;
    flex: 0 0 100%;
    margin-top: ${tokens.space['8']};
  `
      : `
    flex: 1 1 auto;
    margin: 0 ${tokens.space['8']} 0 0;
  `}
`;

const Fill = styled.span`
  display: block;
  height: 100%;
  border-radius: inherit;
`;

const RowValue = styled.div<{ $density: RankedBarListDensity }>`
  flex: 0 0 auto;
  text-align: right;
  white-space: nowrap;

  ${({ $density }) =>
    $density === 'breakdown'
      ? `
    order: 1;
    margin-left: ${tokens.space['16']};
  `
      : /* The compact ranking is a single line per rank — stacking the count over its share would
           put the row pitch at twice the 23px the screens draw. Safari < 14.1 has no flex gap
           (conventions/components/cross-browser.md § 5). */
        `
    display: flex;
    align-items: baseline;

    > * + * {
      margin-left: ${tokens.space['4']};
    }
  `}
`;

const ValueCount = styled.span<{
  $density: RankedBarListDensity;
  $fontSize?: string;
  $lineHeight?: string;
  $color?: string;
}>`
  display: block;
  color: ${({ $color }) => $color ?? tokens.color['text-primary']};
  font-size: ${({ $density, $fontSize }) =>
    $fontSize ??
    ($density === 'breakdown' ? tokens.type['card-title-size'] : tokens.type['label-size'])};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${({ $density, $lineHeight }) =>
    $lineHeight ??
    ($density === 'breakdown' ? tokens.type['card-title-line'] : tokens.type['label-line'])};
`;

const ValuePercent = styled.span`
  display: block;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const RankedBarList: React.FC<IRankedBarListProps> = ({
  items,
  density,
  printShare = true,
  labelColumnWidth = LABEL_COLUMN_WIDTH,
  palette = COMPACT_BAR_PALETTE,
  trackHeight,
  trackRadius,
  labelFontSize,
  labelLineHeight,
  valueFontSize,
  valueLineHeight,
  valueColor,
  viewMore,
  className
}) => {
  const rows = Array.isArray(items) ? items : [];

  // An empty ranking leaves no shell behind: the card shows its own state block instead.
  if (rows.length === 0) {
    return null;
  }

  // One renderer, called twice: the overlay's rows cannot drift from the card's.
  const drawList = (drawn: readonly IRankedBarListItem[]) => (
    <List
      $density={density}
      className={className}
      data-density={density}
      data-testid="ranked-bar-list"
    >
      {drawn.map((row, index) => {
        const hasShare = Number.isFinite(row.pct);
        const fill =
          row.color || (density === 'compact' ? palette[index % palette.length] : BAR_FILL);

        return (
          // Position, not label: a ranking may legitimately repeat a label, and the list is rendered
          // in the order it is given rather than reordered in place.
          <Row key={index} $density={density} data-testid="ranked-bar-list-item">
            <RowText
              $density={density}
              $labelColumn={labelColumnWidth}
              data-testid="ranked-bar-list-text"
            >
              <RowLabel
                $density={density}
                $fontSize={labelFontSize}
                $lineHeight={labelLineHeight}
                data-testid="ranked-bar-list-label"
              >
                {row.label}
              </RowLabel>
              {row.subLabel ? (
                <RowSubLabel data-testid="ranked-bar-list-sub-label">{row.subLabel}</RowSubLabel>
              ) : null}
            </RowText>
            <Track
              $density={density}
              $height={trackHeight}
              $radius={trackRadius}
              // The share is printed beside the bar, so a second announcement of it says the
              // same thing twice.
              aria-hidden="true"
              data-testid="ranked-bar-list-track"
            >
              <Fill
                style={{
                  width: `${hasShare ? sharePercent(row.pct) : 0}%`,
                  background: fill
                }}
                data-color={fill}
                data-testid="ranked-bar-list-fill"
              />
            </Track>
            <RowValue $density={density}>
              {row.value === undefined ? null : (
                <ValueCount
                  $density={density}
                  $fontSize={valueFontSize}
                  $lineHeight={valueLineHeight}
                  $color={valueColor}
                  data-testid="ranked-bar-list-value"
                >
                  {row.value}
                </ValueCount>
              )}
              {hasShare && printShare ? (
                <ValuePercent data-testid="ranked-bar-list-pct">
                  {formatSharePercent(row.pct)}
                </ValuePercent>
              ) : null}
            </RowValue>
          </Row>
        );
      })}
    </List>
  );

  return (
    <>
      {drawList(inlineRowsFor(rows, viewMore))}
      {hiddenRowCount(rows, viewMore) > 0 ? (
        <ListViewMoreFooter rows={rows} viewMore={viewMore} renderFull={() => drawList(rows)} />
      ) : null}
    </>
  );
};

export { BOARD_RISK_PALETTE, BOARD_RAMP_PALETTE };

export default RankedBarList;
